import { Inject, Injectable } from '@angular/core';
import { AuxiliaryFieldSchemaApiService } from '@vendasta/account-group';
import { AuxiliaryDataFieldType } from '@vendasta/account-group/lib/_internal/enums/fields.enum';
import { ListAuxiliaryDataFieldSchemaResponseInterface } from '@vendasta/account-group/lib/_internal/interfaces/fields.interface';
import { BehaviorSubject, Observable, combineLatest, forkJoin } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FieldSelectedForEditService as CustomFieldEditorNotificationsService } from '@vendasta/auxiliary-data-components';
import { FieldMappingOption, doNotImportOptionId, fieldOptions } from './header-mapping.options';

@Injectable()
export class BulkImportMappingService {
  private _mapping$$: BehaviorSubject<Map<string, MappedField>> = new BehaviorSubject(new Map<string, MappedField>());
  customFieldOptions$: Observable<FieldMappingOption[]> = this._customFieldOptions$().pipe(
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
  private createCustomFieldsForCsvColumns$$: BehaviorSubject<Map<string, NewCustomFieldToCreate>> = new BehaviorSubject(
    new Map<string, NewCustomFieldToCreate>(),
  );

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly schemaService: AuxiliaryFieldSchemaApiService,
    private readonly customFieldEditorNotificationsService: CustomFieldEditorNotificationsService,
  ) {}

  get mapping$(): Observable<Map<string, MappedField>> {
    return this._mapping$$.asObservable();
  }

  get mapping(): Map<string, MappedField> {
    return this._mapping$$.getValue();
  }

  set mapping(value: Map<string, MappedField>) {
    this._mapping$$.next(value);
  }

  addToMap(column: string, field: string, ft: fieldType): void {
    const tempMapping = this.mapping;
    tempMapping.set(column, {
      id: field,
      fieldType: ft,
    });
    this.mapping = tempMapping;
  }

  removeFromMap(column: string): void {
    const tempMapping = this.mapping;
    tempMapping.delete(column);
    this._mapping$$.next(tempMapping);
  }

  autoMatch(columnId: string): Observable<string> {
    return this.customFieldOptions$.pipe(
      map((customFieldOptions) => {
        for (const fieldOption of fieldOptions) {
          if (fieldOption.mappings.includes(columnId.toLowerCase())) {
            this.addToMap(columnId, fieldOption.id, 'standard');
            return fieldOption.id;
          }
        }
        for (const customFieldOption of customFieldOptions) {
          if (customFieldOption.mappings.includes(columnId.toLowerCase())) {
            this.addToMap(columnId, customFieldOption.id, 'custom');
            return customFieldOption.id;
          }
        }
        return doNotImportOptionId;
      }),
    );
  }

  getCustomFieldIdByName(name: string): Observable<string> {
    return this.customFieldOptions$.pipe(
      map((customFieldOptions) => {
        for (const customFieldOption of customFieldOptions) {
          if (customFieldOption.mappings.includes(name.toLowerCase())) {
            this.addToMap(name, customFieldOption.id, 'custom');
            return customFieldOption.id;
          }
        }
        return null;
      }),
    );
  }

  getMissingRequiredFields(): string[] {
    const errorFields = [];
    if (!this.isSet('CompanyName')) {
      errorFields.push('CompanyName');
    }
    if (!this.isSet('Zip')) {
      errorFields.push('Zip');
    }
    return errorFields;
  }

  resetMapping(): void {
    this._mapping$$ = new BehaviorSubject(new Map<string, MappedField>());
    this.createCustomFieldsForCsvColumns$$ = new BehaviorSubject(new Map<string, NewCustomFieldToCreate>());
  }

  private isSet(fieldId: string): boolean {
    const mappedFields = Array.from(this.mapping.values());
    const mappedFieldIds = new Set(mappedFields.map((field) => field.id));
    return mappedFieldIds.has(fieldId);
  }

  columnThatsMappedToField(fieldId: string): string {
    let foundColumn = null;
    this.mapping.forEach((field, column) => {
      if (field.id === fieldId) {
        foundColumn = column;
      }
    });
    return foundColumn;
  }

  mapColumnToNewCustomField(column: string, newFieldType: FieldTypesCreatableFromCsv): void {
    const tempMap = this.createCustomFieldsForCsvColumns$$.getValue();
    tempMap.set(column, {
      originalColumn: column,
      fieldName: column,
      fieldType: newFieldType,
    });
    this.createCustomFieldsForCsvColumns$$.next(tempMap);
  }

  doNotMapColumnToNewCustomField(column: string): void {
    const tempMap = this.createCustomFieldsForCsvColumns$$.getValue();
    tempMap.delete(column);
    this.createCustomFieldsForCsvColumns$$.next(tempMap);
  }

  // returns columns that errored
  createCustomFields(): Observable<string[]> {
    return this.createCustomFieldsForCsvColumns$$.pipe(
      map((fieldsToCreate) => Array.from(fieldsToCreate.values())),
      switchMap((fieldsToCreate: NewCustomFieldToCreate[]) =>
        forkJoin(fieldsToCreate.map((field) => this.createCustomField(field.originalColumn, field))),
      ),
    );
  }

  // returns column ID if it errored, otherwise null
  private createCustomField(column: string, newField: NewCustomFieldToCreate): Observable<string> {
    return this.partnerId$.pipe(
      switchMap((pid) =>
        this.schemaService.createAuxiliaryDataFieldSchema({
          fieldSchema: {
            fieldName: column,
            fieldType: newField.fieldType,
            partnerId: pid,
          },
        }),
      ),
      map(() => null),
      catchError(() => column),
    );
  }

  private toObject(ft: fieldType): Record<string, string> {
    const obj = {};
    this.mapping.forEach((field, column) => {
      if (field.fieldType === ft) {
        obj[field.id] = column;
      }
    });
    return obj;
  }

  standardFieldsObject(): Record<string, string> {
    return this.toObject('standard');
  }

  customFieldsObject(): Record<string, string> {
    return this.toObject('custom');
  }

  private _customFieldOptions$(): Observable<FieldMappingOption[]> {
    return combineLatest([
      this.partnerId$,
      this.customFieldEditorNotificationsService.fieldCreated$$.pipe(startWith('initial load for page')),
    ]).pipe(
      switchMap(([partnerId]) =>
        this.schemaService
          .listAuxiliaryDataFieldSchema({
            partnerId: partnerId,
            pagingOptions: {
              cursor: '',
              pageSize: 100,
            },
          })
          .pipe(take(1)),
      ),
      map((customFields: ListAuxiliaryDataFieldSchemaResponseInterface) => {
        if (!customFields?.fieldSchemas?.length) {
          return [];
        }
        return customFields?.fieldSchemas.map(
          (schema) =>
            new FieldMappingOption(schema.fieldId, schema.fieldName, generateMappings(schema.fieldName), true),
        );
      }),
      catchError(() => {
        console.warn('failed to load custom fields for CSV mapping');
        return [];
      }),
    );
  }
}

export type fieldType = 'standard' | 'custom';

interface MappedField {
  fieldType: fieldType;
  id: string;
}

type FieldTypesCreatableFromCsv =
  | AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING
  | AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER
  | AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE;

interface NewCustomFieldToCreate {
  originalColumn: string;
  fieldName: string;
  fieldType: FieldTypesCreatableFromCsv;
}

function generateMappings(fieldName: string): string[] {
  const nameParts = fieldName.toLowerCase().split(' ');
  return [nameParts.join('-'), nameParts.join(' '), nameParts.join('_'), nameParts.join('')];
}
